import type { OfferFormDataT } from "@/app/types/offers";

export const formatCurrency = (
  value: number | null | undefined,
  fractionDigits = 2
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return value.toLocaleString("de-DE", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

export const formatPrice = (
  value: number | null | undefined,
  fractionDigits = 2
): string | null => {
  const formatted = formatCurrency(value, fractionDigits);
  return formatted ? `${formatted} €` : null;
};

/**
 * Format price with fallback to "0,00 €"
 */
export const formatPriceWithFallback = (
  price: number | null | undefined
): string => {
  const formatted = formatPrice(price);
  // TODO: Vielleicht ein * hinzufügen bei fallback mit verlinkung auf legals text sodass bei fehlanzeige kein rechtlicher anspruch besteht?
  return formatted ?? "0,00 €";
};

/**
 * Format fuel consumption with fallback to "-"
 * Formats fuel consumption values in l/100 km
 * Removes trailing zeros (e.g., 12.30 becomes 12.3)
 */
export const formatFuelConsumption = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  const formatted = value.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${formatted} l/100 km`;
};

/**
 * Format CO₂ emission with fallback to "-"
 * Formats CO₂ emission values in g/km
 * Removes trailing zeros (e.g., 12.30 becomes 12.3)
 */
export const formatCo2Emission = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  const formatted = value.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${formatted} g/km`;
};

/**
 * Format electric range with fallback to "-"
 * Formats electric range values in km (no decimal places)
 */
export const formatElectricRange = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  const formatted = value.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${formatted} km`;
};

/**
 * Format electric consumption with fallback to "-"
 * Formats electric consumption values in kWh/100 km
 * Removes trailing zeros (e.g., 12.30 becomes 12.3)
 */
export const formatElectricConsumption = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  const formatted = value.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${formatted} kWh/100 km`;
};

/**
 * Format combined hybrid consumption (electric + fuel)
 * Returns format like "21,9 kWh/100 km plus 0,8 l/100 km"
 */
export const formatHybridConsumptionCombined = (
  electricConsumption: number | null | undefined,
  fuelConsumption: number | null | undefined
): string => {
  const electric = formatElectricConsumption(electricConsumption);
  const fuel = formatFuelConsumption(fuelConsumption);

  const parts: string[] = [];

  if (electric !== "-") {
    parts.push(electric);
  }

  if (fuel !== "-") {
    parts.push(fuel);
  }

  if (parts.length === 0) {
    return "-";
  }

  return parts.join(" plus ");
};

export const formatPercentage = (
  value: number | null | undefined,
  fractionDigits = 2
): string | null => {
  // TODO: Vielleicht ein * hinzufügen bei fallback mit verlinkung auf legals text sodass bei fehlanzeige kein rechtlicher anspruch besteht?
  if (value === null || value === undefined) {
    return "0,00 %";
  }

  return `${value.toLocaleString("de-DE", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} %`;
};

export const formatNumberWithUnit = (
  value: number | null | undefined,
  unit: string,
  fractionDigits = 2
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return `${value.toLocaleString("de-DE", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} ${unit}`;
};

export const formatMileage = (
  value: number | null | undefined
): string | null => {
  // TODO: Vielleicht ein * hinzufügen bei fallback mit verlinkung auf legals text sodass bei fehlanzeige kein rechtlicher anspruch besteht?
  if (value === null || value === undefined) {
    return "0 km";
  }

  return `${value.toLocaleString("de-DE")} km`;
};

// Format vehicle details based on real data
export const getStatusTag = (offer: OfferFormDataT) => {
  if (offer.vehicle_condition === "used") {
    return "gebrauchtwagen";
  }
  if (offer.vehicle_condition === "new") {
    if (offer.vehicle_availability === "order") {
      return "bestellfahrzeug";
    } else {
      return "sofort-verfuegbar";
    }
  }
  // Default fallback
  return "neuwagen";
};
